import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Star, Target, CheckCircle, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function ConfidenceAssessmentGuide() {
  const navigate = useNavigate();

  const confidenceAreas = [
    { area: "Work/Career", questions: ["Speaking up in meetings", "Asking for what you need", "Taking on new challenges", "Accepting compliments"] },
    { area: "Social Situations", questions: ["Meeting new people", "Starting conversations", "Being yourself around others", "Handling disagreements"] },
    { area: "Physical Appearance", questions: ["How you look in photos", "Wearing bold clothing", "Going makeup-free", "Aging gracefully"] },
    { area: "Speaking Up", questions: ["Sharing your opinions", "Setting boundaries", "Saying no", "Asking for help"] },
    { area: "Trying New Things", questions: ["Learning new skills", "Visiting new places", "Changing routines", "Taking risks"] }
  ];

  const confidenceBuilders = [
    { action: "Celebrate small wins", how: "Keep a 'wins' journal—write 3 things you did well each day" },
    { action: "Power pose before challenges", how: "Stand tall, hands on hips, for 2 minutes before stressful situations" },
    { action: "Prepare and practice", how: "Rehearse important conversations or presentations out loud" },
    { action: "Reframe negative self-talk", how: "Replace 'I can't' with 'I'm learning to'" },
    { action: "Take small risks daily", how: "Do one thing that slightly scares you every day" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold mb-4">
            Week 7 • Day 1 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Confidence Self-Assessment
          </h1>
          <p className="text-xl text-secondary-600">Know Your Strengths and Growth Areas</p>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <Star className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">Today's Exercise</h3>
              <p className="text-amber-100 text-lg">
                Rate your confidence (1-10) in each area below. Circle the lowest two. 
                These are your growth opportunities for this week.
              </p>
            </div>
          </div>
        </section>

        {/* Assessment */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">Rate Your Confidence (1-10)</h2>
          <div className="space-y-6">
            {confidenceAreas.map((item, index) => (
              <div key={index} className="bg-amber-50 rounded-xl p-5">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-lg font-bold text-amber-700">{item.area}</h3>
                  <div className="flex gap-1">
                    {[1,2,3,4,5,6,7,8,9,10].map(n => (
                      <div key={n} className="w-6 h-6 border border-amber-300 rounded text-center text-xs flex items-center justify-center text-amber-600">{n}</div>
                    ))}
                  </div>
                </div>
                <ul className="grid md:grid-cols-2 gap-2">
                  {item.questions.map((q, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-secondary-700">
                      <CheckCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                      {q}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Confidence Builders */}
        <section className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 border-2 border-amber-200">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6 flex items-center gap-2">
            <Heart className="w-6 h-6 text-amber-500" />
            Confidence Builders
          </h2>
          <div className="space-y-4">
            {confidenceBuilders.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-4">
                <h3 className="font-bold text-amber-700 mb-1">{item.action}</h3>
                <p className="text-sm text-secondary-600">{item.how}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default ConfidenceAssessmentGuide;
